import { useAccess, canVisit } from '@/features/access/access';
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRightIcon, SearchIcon } from '@/components/icons';
import { searchQuickNavigation } from './quick-navigation';
import './HeaderSearch.css';

export const HeaderSearch = () => {
  const access = useAccess();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const results = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const items = searchQuickNavigation(query).filter((item) => canVisit(access.data, item.to));
  const select = (to: string) => {
    setOpen(false);
    setQuery('');
    input.current?.blur();
    void navigate(to);
  };

  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === 'k' &&
        !document.querySelector('dialog[open]')
      ) {
        event.preventDefault();
        input.current?.focus();
        input.current?.select();
        setOpen(true);
      }
    };
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('keydown', shortcut);
    document.addEventListener('pointerdown', outside);
    return () => {
      document.removeEventListener('keydown', shortcut);
      document.removeEventListener('pointerdown', outside);
    };
  }, []);

  useEffect(() => {
    if (open)
      results.current
        ?.querySelector('[aria-selected="true"]')
        ?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open, query]);

  return (
    <div
      ref={root}
      className="header-search"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <div className="header-search-field" data-open={open || undefined}>
        <SearchIcon />
        <input
          ref={input}
          type="search"
          role="combobox"
          aria-label="Rechercher une page ou une action"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? id : undefined}
          aria-activedescendant={open && items[active] ? `${id}-${active}` : undefined}
          autoComplete="off"
          placeholder="Rechercher une page, une action…"
          value={query}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return;
            if (event.key === 'Escape') {
              event.preventDefault();
              setOpen(false);
            }
            if (event.key === 'Tab') setOpen(false);
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              if (items.length)
                setActive(
                  (current) =>
                    (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length,
                );
            }
            if (event.key === 'Enter' && open && items[active]) {
              event.preventDefault();
              select(items[active].to);
            }
          }}
        />
        <kbd aria-hidden="true">Ctrl / ⌘ K</kbd>
      </div>
      {open && (
        <div className="header-search-panel">
          <div className="header-search-caption">
            Accès rapide{' '}
            <span>
              {items.length} résultat{items.length > 1 ? 's' : ''}
            </span>
          </div>
          <div
            ref={results}
            id={id}
            role="listbox"
            aria-label="Pages et actions"
            className="header-search-results"
          >
            {['Actions rapides', 'Pages'].map((group) => {
              const groupItems = items.filter((item) => item.group === group);
              if (!groupItems.length) return null;
              return (
                <div role="group" aria-label={group} key={group}>
                  <div className="header-search-group" aria-hidden="true">
                    {group}
                  </div>
                  {groupItems.map((item) => {
                    const index = items.indexOf(item);
                    const Icon = item.icon;
                    return (
                      <button
                        type="button"
                        role="option"
                        id={`${id}-${index}`}
                        key={item.to}
                        aria-selected={active === index}
                        tabIndex={-1}
                        className="header-search-option"
                        onPointerMove={() => setActive(index)}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => select(item.to)}
                      >
                        <span className="header-search-option-icon">
                          <Icon />
                        </span>
                        <span className="header-search-option-text">
                          <strong>{item.label}</strong>
                          <small>{item.detail}</small>
                        </span>
                        <ArrowRightIcon className="header-search-arrow" />
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {!items.length && (
            <p className="header-search-empty" role="status">
              Aucun résultat. Essayez « candidat », « société » ou « facture ».
            </p>
          )}
          <div className="header-search-footer">
            <span>↑ ↓ Naviguer</span>
            <span>↵ Ouvrir</span>
            <span>Échap Fermer</span>
          </div>
        </div>
      )}
    </div>
  );
};
